import graphql__165141b55689aac58f6e from "./__generated__/AppVisibilityFormUpdateVisibilityMutation.graphql.ts";import {
  Button,
  InputError,
  MessagePanel,
  RadioButtonList,
} from '@portal/components'
import { graphql } from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import { useFormik } from 'formik'
import { startCase, toLower } from 'lodash'
import { useState } from 'react'
import { RRNLRequestError } from 'react-relay-network-modern'
import { readableErrorMessage } from 'scenes/appManagement/tabs/auth/apiKey/utils'
import { usePortalMutation } from 'utils'
import * as Yup from 'yup'

import {
  AppVisibility,
  AppVisibilityFormUpdateVisibilityMutation,
} from './__generated__/AppVisibilityFormUpdateVisibilityMutation.graphql'

type FormValues = {
  appVisibility: AppVisibility
}

const visibilityOptions = (
  databaseText: string,
): Partial<Record<AppVisibility, string>> => ({
  EVERYONE: `Anyone in your organization can see the ${databaseText}`,
  ONLY_OWNER: `Only the organization owner can see the ${databaseText}`,
  OWNER_OR_ADMIN: `The organization owner or admin(s) can see the ${databaseText}`,
})

const AppFormSchema = Yup.object().shape({
  appVisibility: Yup.mixed<AppVisibility>().oneOf([
    'EVERYONE',
    'ONLY_OWNER',
    'OWNER_OR_ADMIN',
  ]),
})
const updateVisibilityMutation = graphql__165141b55689aac58f6e

type Props = {
  appId: string
  appVisibility: AppVisibility
}

export default function AppVisibilityForm({ appId, appVisibility }: Props) {
  const { databaseText, isRenamed } = useAppToDatabaseRename()
  const [updateApp, isUpdatingApp] =
    usePortalMutation<AppVisibilityFormUpdateVisibilityMutation>(
      updateVisibilityMutation,
      {
        mutationName: 'UpdateAppVisibility',
        sentryContext: {
          component: 'AppVisibilityForm',
          appId,
        },
        showErrorToast: false,
      },
    )
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string>()

  const labelText = isRenamed ? 'Database Visibility' : 'App Visibility'

  const formik = useFormik<FormValues>({
    initialValues: {
      appVisibility,
    },
    validationSchema: AppFormSchema,
    onSubmit: (values) => {
      if (updateErrorMessage) {
        setUpdateErrorMessage(undefined)
      }
      if (appVisibility === values.appVisibility) {
        return
      }
      updateApp({
        variables: {
          appId,
          appVisibility: values.appVisibility,
        },
        onError: (err) => {
          if (err instanceof RRNLRequestError) {
            setUpdateErrorMessage(readableErrorMessage(err))
          } else {
            setUpdateErrorMessage(err.message)
          }
        },
      })
    },
  })

  const submitButtonTitle = () => {
    if (isUpdatingApp) {
      return 'Update in progress...'
    } else if (appVisibility === formik.values.appVisibility) {
      return 'No changes selected in form.'
    } else {
      return 'Save your changes.'
    }
  }

  return (
    <>
      {updateErrorMessage && !isUpdatingApp ? (
        <MessagePanel
          message={updateErrorMessage}
          variant="danger"
          className="my-3"
        />
      ) : null}

      <form
        id="changeAppVisibilityForm"
        className="mt-4 max-w-xl"
        onSubmit={formik.handleSubmit}
      >
        <RadioButtonList<AppVisibility>
          value={formik.values.appVisibility}
          options={['EVERYONE', 'OWNER_OR_ADMIN', 'ONLY_OWNER']}
          keyFn={(option) => option}
          label={labelText}
          renderFn={(option) => {
            const lowerCaseOption = toLower(option)
            return {
              title: startCase(lowerCaseOption),
              description: visibilityOptions(databaseText)[option] || '',
            }
          }}
          onChange={formik.handleChange}
          htmlFor="appVisibility"
          groupDisabled={isUpdatingApp}
        />

        {formik.errors.appVisibility && formik.touched.appVisibility ? (
          <InputError
            message={formik.errors.appVisibility}
            className="w-full"
          />
        ) : null}
      </form>

      <Button
        variant="primary"
        form="changeAppVisibilityForm"
        title={submitButtonTitle()}
        type="submit"
        disabled={isUpdatingApp}
        className="mt-8 py-2.5"
      >
        Save Changes
      </Button>
    </>
  )
}
